/**
 * Sidebar Flyout Components
 *
 * Provides flyout menu functionality for the sidebar in mini mode.
 * Handles hover/click interactions with proper touch device support.
 */

import type Alpine from "alpinejs";
import type { FlyoutOptions, TooltipOptions } from "./types";

/**
 * Alpine.js component with $el magic property.
 *
 * Alpine injects $el at runtime - we use this interface to access it type-safely.
 */
interface AlpineThis {
  $el: HTMLElement;
}

import {
  CLOSE_DELAY_MS,
  DEFAULT_FLYOUT_LEFT,
  FLYOUT_NAV_GAP,
  FLYOUT_OFFSET_X,
  FLYOUT_OFFSET_Y,
  TOOLTIP_DELAY_MS,
} from "./config";
import { checkMiniMode, isTouchDevice } from "./utils";

/**
 * Alpine.js component for flyout menus with dropdowns.
 *
 * Usage:
 * ```html
 * <li x-data="sidebarFlyout({ menuId: 'samai' })">
 *   <button @mouseenter="handleMouseEnter" ...>
 *   <div x-show="isOpen && isMiniMode" ...>Flyout content</div>
 * </li>
 * ```
 */
export function sidebarFlyout(options: FlyoutOptions) {
  return {
    isOpen: false,
    isMiniMode: false,
    isDropdownExpanded: false, // Tracks inline dropdown state (expanded mode)
    isTouchDevice: isTouchDevice(),
    menuId: options.menuId,
    flyoutTop: 0, // Reactive property for vertical positioning
    flyoutLeft: DEFAULT_FLYOUT_LEFT, // Default offset to match mini sidebar width
    closeTimeout: null as ReturnType<typeof setTimeout> | null,
    _observer: null as MutationObserver | null,
    _dropdownObserver: null as MutationObserver | null,
    _resizeHandler: null as (() => void) | null,
    _closeAllHandler: null as ((e: Event) => void) | null,
    _clickOutsideHandler: null as ((e: Event) => void) | null,
    _escapeHandler: null as ((e: KeyboardEvent) => void) | null,

    init() {
      this.updateMiniMode();

      // Listen for sidebar state changes via MutationObserver
      const sidebar = document.getElementById("sidebar");
      if (sidebar) {
        this._observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.attributeName === "data-sidebar-state") {
              this.updateMiniMode();
              if (!this.isMiniMode) {
                this.closeFlyout();
              }
            }
          }
        });

        this._observer.observe(sidebar, { attributes: true });
      }

      // Listen for window resize to update mini mode state
      this._resizeHandler = () => this.updateMiniMode();
      window.addEventListener("resize", this._resizeHandler);

      // Listen for close-all events (for touch device mutual exclusion)
      this._closeAllHandler = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail?.except !== this.menuId && this.isOpen) {
          this.closeFlyout();
        }
      };
      document.addEventListener("sidebar-flyout:close-all", this._closeAllHandler);

      // Close on click outside
      this._clickOutsideHandler = (e: Event) => {
        if (!this.isOpen) return;

        const target = e.target as HTMLElement;
        const flyout = document.getElementById(`flyout-${this.menuId}`);
        const trigger = (this as unknown as AlpineThis).$el;

        if (flyout && !flyout.contains(target) && !trigger.contains(target)) {
          this.closeFlyout();
        }
      };
      document.addEventListener("click", this._clickOutsideHandler);

      // Close on Escape key
      this._escapeHandler = (e: KeyboardEvent) => {
        if (e.key === "Escape" && this.isOpen) {
          this.closeFlyout();
        }
      };
      document.addEventListener("keydown", this._escapeHandler);

      // Track inline dropdown expansion state (for chevron rotation in expanded mode)
      const dropdown = document.getElementById(`dropdown-${this.menuId}`);
      if (dropdown) {
        // Check initial state
        this.isDropdownExpanded = !dropdown.classList.contains("hidden");

        // Watch for Flowbite's class changes
        this._dropdownObserver = new MutationObserver(() => {
          this.isDropdownExpanded = !dropdown.classList.contains("hidden");
        });
        this._dropdownObserver.observe(dropdown, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    },

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
      }
      if (this._dropdownObserver) {
        this._dropdownObserver.disconnect();
      }
      if (this._resizeHandler) {
        window.removeEventListener("resize", this._resizeHandler);
      }
      if (this._closeAllHandler) {
        document.removeEventListener("sidebar-flyout:close-all", this._closeAllHandler);
      }
      if (this._clickOutsideHandler) {
        document.removeEventListener("click", this._clickOutsideHandler);
      }
      if (this._escapeHandler) {
        document.removeEventListener("keydown", this._escapeHandler);
      }
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
      }
    },

    handleMouseEnter() {
      // Don't handle hover on touch devices
      if (this.isTouchDevice) return;
      if (!this.isMiniMode) return;

      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      if (this.isOpen) return;
      this.openFlyout();
    },

    handleMouseLeave() {
      if (this.isTouchDevice) return;
      if (!this.isMiniMode) return;

      // Delay close to allow moving mouse to flyout
      this.closeTimeout = setTimeout(() => {
        this.closeFlyout();
      }, CLOSE_DELAY_MS);
    },

    handleClick(event: MouseEvent) {
      // On touch devices in mini mode, toggle flyout
      if (this.isTouchDevice && this.isMiniMode) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isOpen) {
          this.closeFlyout();
        } else {
          // Close any other open flyouts first
          document.dispatchEvent(
            new CustomEvent("sidebar-flyout:close-all", {
              detail: { except: this.menuId },
            }),
          );
          this.openFlyout();
        }
        return;
      }

      // In expanded mode, let the existing Flowbite collapse handle it
    },

    handleFocus() {
      if (this.isTouchDevice) return;
      if (!this.isMiniMode) return;
      this.openFlyout();
    },

    handleBlur() {
      if (this.isTouchDevice) return;
      // Delay close to allow focus to move to flyout items
      setTimeout(() => {
        const flyout = document.getElementById(`flyout-${this.menuId}`);
        if (flyout && !flyout.contains(document.activeElement)) {
          this.closeFlyout();
        }
      }, 50);
    },

    updateFlyoutPosition(): void {
      const el = (this as unknown as AlpineThis).$el;
      const fallbackRect = el.getBoundingClientRect();
      const trigger = el.querySelector("button, a");
      const triggerRect = trigger?.getBoundingClientRect() ?? fallbackRect;
      const nav = document.getElementById("main-navbar");
      const navBottom = nav ? nav.getBoundingClientRect().bottom : 0;
      const minTop = navBottom + FLYOUT_NAV_GAP;

      let top = triggerRect.top + FLYOUT_OFFSET_Y;
      if (top < minTop) {
        top = minTop;
      }

      this.flyoutTop = Math.round(top);
      this.flyoutLeft = Math.round(triggerRect.right + FLYOUT_OFFSET_X);
    },

    openFlyout() {
      // Calculate position BEFORE opening so Alpine has the value when rendering
      this.updateFlyoutPosition();
      this.isOpen = true;

      // Use setTimeout to defer until after Alpine renders (Alpine has no $nextTick)
      setTimeout(() => {
        const flyoutEl = document.getElementById(`flyout-${this.menuId}`);
        if (flyoutEl) {
          flyoutEl.style.top = `${this.flyoutTop}px`;
          flyoutEl.style.left = `${this.flyoutLeft}px`;
        }
      }, 0);
    },

    closeFlyout() {
      this.isOpen = false;
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    },

    updateMiniMode() {
      this.isMiniMode = checkMiniMode();
    },
  };
}

/**
 * Alpine.js component for tooltip-only items (no dropdown).
 *
 * Usage:
 * ```html
 * <li x-data="sidebarTooltip({ label: 'Dashboard' })">
 *   <a @mouseenter="showTooltip" @mouseleave="hideTooltip" ...>
 *   <div x-show="isVisible && isMiniMode" ...>{{ label }}</div>
 * </li>
 * ```
 */
export function sidebarTooltip(options: TooltipOptions) {
  return {
    isVisible: false,
    isMiniMode: false,
    label: options.label,
    tooltipTop: 0, // Reactive property for vertical positioning (centered)
    hideTimeout: null as ReturnType<typeof setTimeout> | null,
    _observer: null as MutationObserver | null,
    _resizeHandler: null as (() => void) | null,

    init() {
      this.updateMiniMode();

      const sidebar = document.getElementById("sidebar");
      if (sidebar) {
        this._observer = new MutationObserver(() => {
          this.updateMiniMode();
          if (!this.isMiniMode) {
            this.isVisible = false;
          }
        });

        this._observer.observe(sidebar, { attributes: true });
      }

      this._resizeHandler = () => this.updateMiniMode();
      window.addEventListener("resize", this._resizeHandler);
    },

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
      }
      if (this._resizeHandler) {
        window.removeEventListener("resize", this._resizeHandler);
      }
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
      }
    },

    showTooltip() {
      if (!this.isMiniMode) return;

      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }

      // Calculate position BEFORE showing so Alpine has the value when rendering
      const el = (this as unknown as AlpineThis).$el;
      const trigger = el.querySelector("a");
      if (trigger) {
        const triggerRect = trigger.getBoundingClientRect();
        this.tooltipTop = triggerRect.top + triggerRect.height / 2;
      }

      this.isVisible = true;

      // Use setTimeout to defer until after Alpine renders (Alpine has no $nextTick)
      setTimeout(() => {
        const tooltipEl = el.querySelector('[role="tooltip"]') as HTMLElement;
        if (tooltipEl) {
          tooltipEl.style.top = `${this.tooltipTop}px`;
        }
      }, 0);
    },

    hideTooltip() {
      this.hideTimeout = setTimeout(() => {
        this.isVisible = false;
      }, TOOLTIP_DELAY_MS);
    },

    updateMiniMode() {
      this.isMiniMode = checkMiniMode();
    },
  };
}

/**
 * Register Alpine.js components for sidebar flyouts.
 *
 * Call this during Alpine initialization:
 * ```javascript
 * document.addEventListener("alpine:init", () => {
 *   registerSidebarFlyoutComponents(Alpine);
 * });
 * ```
 */
export function registerSidebarFlyoutComponents(alpine: typeof Alpine): void {
  alpine.data("sidebarFlyout", sidebarFlyout);
  alpine.data("sidebarTooltip", sidebarTooltip);
}
